import { CurrencyPipe, TitleCasePipe, UpperCasePipe } from '@angular/common';
import type { SimpleChanges } from '@angular/core';
import { ChangeDetectionStrategy, Component, inject, Input, output, input } from '@angular/core';
import { Router } from '@angular/router';

import moment from 'moment-timezone';
import { MomentModule } from 'ngx-moment';

import type {
  BillingPeriod,
  BillingPlan,
  PricingFeatureSubscription,
  ScheduledChange,
  SlotsAddon,
  TextingAddon,
} from '@core/billing-types';
import { AddonType, BillingPlanTier } from '@core/billing-types';
import { injectDevFlag } from '@core/dev-flags-logic';
import { DevFlag } from '@core/dev-flags-types';
import { I18nService, TranslatePipe } from '@core/i18n-legacy';
import { IntercomService } from '@core/intercom-integration-legacy';
import { TELLENT_NOT_AUTHORIZED } from '@core/tellent-types';
import { diffDates, TimeAgoPipe } from '@core/time-legacy';
import { DateComponent } from '@core/time-ui';
import {
  BadgeComponent,
  BoxComponent,
  ButtonComponent,
  ButtonLoadingDirective,
  DividerComponent,
  HeadingComponent,
  IconComponent,
  MessageComponent,
} from '@core/user-interface-legacy';
import { BillingCollectNowService } from '@recruitee/billing-logic';

import { getPaymentIntervalText } from '../../../helpers/get-payment-interval-text';
import { getProductCodeOrThrow } from '../../../helpers/get-product-code-or-throw';
import { getProductName } from '../../../helpers/get-product-name';
import { isLowTextingCredits } from '../../../helpers/is-low-texting-credits';
import type { PricingFeatureLoadedModel, Trial } from '../../../types/pricing-feature.types';
import type { NextPaymentInfoUI } from '../billing-page.types';
import { BillingScheduledChangeComponent } from '../billing-scheduled-change/billing-scheduled-change.component';

@Component({
  selector: 'rt-billing-details',
  templateUrl: './billing-details.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    BoxComponent,
    HeadingComponent,
    DividerComponent,
    BadgeComponent,
    ButtonComponent,
    ButtonLoadingDirective,
    IconComponent,
    DateComponent,
    BillingScheduledChangeComponent,
    MessageComponent,
    UpperCasePipe,
    TitleCasePipe,
    CurrencyPipe,
    TranslatePipe,
    MomentModule,
    TimeAgoPipe,
  ],
})
export class BillingDetailsComponent {
  private readonly i18n = inject(I18nService);
  private readonly router = inject(Router);
  private readonly collectNowSvc = inject(BillingCollectNowService);

  public readonly collectNowLoading = this.collectNowSvc.loading;
  public readonly bnplEnabled = injectDevFlag(DevFlag.AcquisitionBuyNowPayLater);
  public readonly intercomService = inject(IntercomService);
  public readonly tsEnabledButUnauthorized = inject(TELLENT_NOT_AUTHORIZED);

  public readonly scheduledChange = input<ScheduledChange | null>();
  public readonly reactivatePlanLoading = input<boolean>(undefined!);
  public readonly canceled = input<boolean>(undefined!);
  public readonly subscription = input<PricingFeatureSubscription>(undefined!);
  public readonly subscriptionStatus = input<PricingFeatureLoadedModel['subscriptionStatus']>(
    undefined!,
  );

  public readonly jobSlotsUsed = input<number>(undefined!);
  public readonly slotsTotal = input<number>(undefined!);
  public readonly manual = input<boolean>(undefined!);
  public readonly isExternalBilling = input<boolean>(undefined!);
  public readonly isUnlimited = input<boolean>(undefined!);
  public readonly isStartPlan = input<boolean>(undefined!);
  public readonly isPP24 = input<boolean>(undefined!);
  public readonly activePlan = input<BillingPlan>(undefined!);

  @Input() public textingAddon: TextingAddon | undefined;
  @Input() public ssoAddon: {
    isActive: boolean;
    isAvailable: boolean;
  };
  @Input() public nextPayment: NextPaymentInfoUI | null;
  @Input() public trial: Trial;

  public editPlan = output<void>();
  public editSlots = output<void>();
  public reactivatePlan = output<void>();
  public cancelSubscription = output<void>();
  public textingSubscriptionChange = output<void>();
  public today: moment.MomentInput;
  public productCode: BillingPlanTier;
  public productName: string;
  public validUntilMoment?: moment.Moment;
  public validUntilDaysLeft: number;
  public showRenewButton: boolean = true;
  public showContactUsButton: boolean;
  public showUpgradeSlotsButton: boolean;
  public isChangeSlotsButtonDisabled = false;
  public isNextPaymentDiscounted = false;
  public isTextingChangeSubscriptionButtonDisabled = false;
  public isTextingSubscriptionInactive = false;
  public nextPaymentAmount: number;
  public nextPaymentSlotsAddon?: SlotsAddon;

  public showCancelledBadge: boolean;
  public isNonRenewing: boolean;

  public isTextingCancellationScheduled: boolean;
  private billingPeriod?: BillingPeriod;

  public ngOnChanges(changes: SimpleChanges) {
    const subscription = this.subscription();
    if (subscription) {
      const { tier, validUntil, currentPeriodEnd, immutable, billingPeriod, status, contractTerm } =
        subscription;
      this.productName = getProductName({ codename: tier }, this.i18n);
      this.productCode = getProductCodeOrThrow(tier);

      const subscriptionPeriodEnd = validUntil ?? currentPeriodEnd;
      this.validUntilMoment = moment(<moment.MomentInput>subscriptionPeriodEnd);

      this.validUntilDaysLeft = diffDates(subscriptionPeriodEnd!)!;

      this.showCancelledBadge = status === 'canceled';
      this.isNonRenewing =
        (status === 'active' && contractTerm?.actionAtTermEnd === 'cancel') ||
        status === 'non_renewing';

      this.showRenewButton = !(this.isNonRenewing && this.manual());

      this.showContactUsButton = this.showContactUs();

      this.showUpgradeSlotsButton = this.canShowUpgradeSlotsButton();
      const subscriptionStatus = this.subscriptionStatus();
      this.isChangeSlotsButtonDisabled =
        subscriptionStatus.canceled || subscriptionStatus.paused || subscriptionStatus.future;

      const subscriptionStatusValue = this.subscriptionStatus();
      this.isTextingChangeSubscriptionButtonDisabled =
        subscriptionStatusValue.canceled ||
        subscriptionStatusValue.paused ||
        subscriptionStatusValue.future ||
        subscriptionStatusValue.blocked ||
        (this.manual() && immutable!);

      const subscriptionStatusVal = this.subscriptionStatus();
      this.isTextingSubscriptionInactive =
        subscriptionStatusVal.paused ||
        subscriptionStatusVal.future ||
        subscriptionStatusVal.blocked;

      if (changes.nextPayment && this.nextPayment) {
        const { total, subTotal, creditsApplied, discounts, paysVat } = this.nextPayment;

        if (paysVat) {
          this.nextPaymentAmount = subTotal + subTotal * 0.21;
        } else {
          this.nextPaymentAmount = total;
        }

        this.isNextPaymentDiscounted = creditsApplied > 0 || discounts > 0;
        this.nextPaymentSlotsAddon = <SlotsAddon>(
          this.nextPayment.addons.find(addon => addon.type === AddonType.Slots)
        );
      }

      this.billingPeriod = billingPeriod;
    }
    const scheduledChange = this.scheduledChange();
    if (changes.scheduledChange && scheduledChange) {
      this.isTextingCancellationScheduled = !!scheduledChange.summarized.addons?.find(
        addon => addon.type === AddonType.Texting && addon.quantity === 0,
      );
    }
  }

  public get intervalText(): string {
    if (this.billingPeriod) {
      return getPaymentIntervalText(this.billingPeriod, this.i18n);
    } else {
      return '';
    }
  }

  public get textingCreditsLeft() {
    return this.textingAddon!.totalCredits - this.textingAddon!.creditsUsed;
  }

  public get lowCredits() {
    return isLowTextingCredits(this.textingCreditsLeft, this.textingAddon!.totalCredits);
  }

  public get contractEnd() {
    return this.subscription().contractTerm?.contractEnd || this.nextPayment?.date || '';
  }

  public get showScheduledChanges() {
    return this.scheduledChange() && this.contractEnd && this.nextPayment;
  }

  public payNow(): void {
    this.collectNowSvc.collectNow();
  }

  public openIntercomWithLegacyMessage() {
    this.intercomService.startNewMessage(
      this.i18n.translate('app.pricing.plan_picker.intercom.legacy_interested_in'),
    );
  }

  public get isBillingPeriodStandard(): boolean {
    return this.billingPeriod ? this.billingPeriod === 1 || this.billingPeriod === 12 : true;
  }

  public get nextPaymentVat() {
    return this.nextPayment?.vatTaxUI || null;
  }

  public ssoCancelSubscription() {
    this.intercomService.startNewMessage(
      this.i18n.translate('app.pricing.plan_picker.intercom.cancel_sso_subscription'),
    );
  }

  public goToSsoSettings() {
    this.router.navigate(['/settings/company/sso']);
  }

  private showContactUs() {
    const subscription = this.subscription();
    if (subscription.immutable) {
      return true;
    }

    if (this.manual()) {
      if (this.isMultiYearContract()) {
        return true;
      } else {
        return (
          [BillingPlanTier.Lead, BillingPlanTier.Optimize].includes(subscription.tier) ||
          this.isNonRenewing
        );
      }
    }

    const subscriptionStatus = this.subscriptionStatus();
    return (
      this.activePlan().custom ||
      subscriptionStatus.paused ||
      subscriptionStatus.future ||
      subscriptionStatus.blocked ||
      !this.isBillingPeriodStandard
    );
  }

  private isMultiYearContract() {
    const subscription = this.subscription();
    if (!subscription.contractTerm) {
      return false;
    }

    const startDate = moment(subscription.contractTerm.contractStart);
    const endDate = moment(subscription.contractTerm.contractEnd);

    return Math.ceil(endDate.diff(startDate, 'year', true)) > 1;
  }

  private canShowUpgradeSlotsButton() {
    if (this.isExternalBilling()) return false;
    if (this.isUnlimited()) return false;
    if (this.subscriptionStatus().blocked) return false;
    if (this.isPP24()) return false;
    return !this.subscription().immutable;
  }
}
